"use client"

import { useState } from "react"
import { Button } from "#/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "#/components/ui/avatar"
import { Badge } from "#/components/ui/badge"
import { LinkIcon, Calendar } from "lucide-react"
import type { User } from "#/lib/types"
import { format } from "date-fns"
import { EditProfileModal } from "./edit-profile-modal"
import { FollowButton } from "#/components/friends/follow-button"

interface ProfileHeaderProps {
  user: User
  isOwnProfile?: boolean
}

export function ProfileHeader({
  user,
  isOwnProfile = false,
}: ProfileHeaderProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  return (
    <div className="space-y-6 mb-8">
      {/* Profile Info */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <Avatar className="h-24 w-24 md:h-32 md:w-32">
          <AvatarImage
            src={user.avatar || "/placeholder.svg"}
            alt={user.displayName}
          />
          <AvatarFallback className="text-2xl">
            {user.displayName[0]}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{user.displayName}</h1>
              {user.isVerified && (
                <Badge
                  variant="secondary"
                  className="bg-primary/10 text-primary"
                >
                  Verified
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">@{user.username}</p>
          </div>

          {/* Stats */}
          <div className="flex gap-6 text-sm">
            <div className="text-center">
              <div className="font-bold text-lg">{user.postCount}</div>
              <div className="text-muted-foreground">Posts</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">
                {user.followerCount.toLocaleString()}
              </div>
              <div className="text-muted-foreground">Followers</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">
                {user.followingCount.toLocaleString()}
              </div>
              <div className="text-muted-foreground">Following</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {isOwnProfile ? (
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(true)}
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <FollowButton
                  username={user.username}
                  className="min-w-[100px]"
                />
                <Button variant="outline">Message</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bio and Details */}
      <div className="space-y-3">
        {user.bio && (
          <p className="text-sm leading-relaxed whitespace-pre-line max-w-2xl">
            {user.bio}
          </p>
        )}

        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          {user.website && (
            <div className="flex items-center gap-1">
              <LinkIcon className="h-4 w-4" />
              <a
                href={`https://${user.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary"
              >
                {user.website}
              </a>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Joined {format(user.createdAt, "MMMM yyyy")}</span>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentBio={user.bio || ""}
      />
    </div>
  )
}
